import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Calendar, TrendingUp, Star, Clock, Activity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface GrowthData {
  date: string;
  new_users: number;
  new_students: number;
  new_mentors: number;
  sessions_booked: number;
  active_users: number;
}

interface GrowthStats {
  totalUsers: number;
  totalStudents: number;
  totalMentors: number;
  userGrowthRate: number;
  averageSessionsPerUser: number;
  mentorUtilizationRate: number;
}

interface SpecialtyData {
  specialty: string;
  count: number;
  sessions: number;
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))', 
  'hsl(var(--accent))',
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#8dd1e1'
];

export function UserGrowthAnalytics() {
  const [growthData, setGrowthData] = useState<GrowthData[]>([]);
  const [specialtyData, setSpecialtyData] = useState<SpecialtyData[]>([]);
  const [stats, setStats] = useState<GrowthStats>({
    totalUsers: 0,
    totalStudents: 0,
    totalMentors: 0,
    userGrowthRate: 0,
    averageSessionsPerUser: 0,
    mentorUtilizationRate: 0
  });
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGrowthData();
  }, [timeRange]);

  const fetchGrowthData = async () => {
    setLoading(true);
    try {
      const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      // Fetch user growth data
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('created_at, role')
        .gte('created_at', startDate.toISOString());

      if (profileError) throw profileError;

      // Fetch sessions data
      const { data: sessions, error: sessionError } = await supabase
        .from('sessions')
        .select('created_at, student_id, mentor_id')
        .gte('created_at', startDate.toISOString());

      if (sessionError) throw sessionError;

      // Group data by date
      const groupedData: { [key: string]: GrowthData } = {};
      
      // Initialize dates
      for (let i = 0; i < daysBack; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        groupedData[dateStr] = {
          date: dateStr,
          new_users: 0,
          new_students: 0,
          new_mentors: 0,
          sessions_booked: 0,
          active_users: 0
        };
      }

      // Count new users by date
      profiles?.forEach(profile => {
        const date = profile.created_at.split('T')[0];
        if (groupedData[date]) {
          groupedData[date].new_users += 1;
          if (profile.role === 'student') {
            groupedData[date].new_students += 1;
          } else if (profile.role === 'mentor') {
            groupedData[date].new_mentors += 1;
          }
        }
      });

      // Count sessions by date
      sessions?.forEach(session => {
        const date = session.created_at.split('T')[0];
        if (groupedData[date]) {
          groupedData[date].sessions_booked += 1;
        }
      });

      const chartData = Object.values(groupedData).reverse();
      setGrowthData(chartData);

      // Calculate overall stats
      const { data: allProfiles } = await supabase
        .from('profiles')
        .select('role');

      const totalUsers = allProfiles?.length || 0;
      const totalStudents = allProfiles?.filter(p => p.role === 'student').length || 0;
      const totalMentors = allProfiles?.filter(p => p.role === 'mentor').length || 0;

      const { data: allSessions } = await supabase
        .from('sessions')
        .select('student_id');

      const averageSessionsPerUser = totalStudents > 0 ? (allSessions?.length || 0) / totalStudents : 0;

      // Calculate growth rate
      const recentUsers = profiles?.length || 0;
      const previousStartDate = new Date();
      previousStartDate.setDate(previousStartDate.getDate() - (daysBack * 2));
      previousStartDate.setDate(previousStartDate.getDate() + daysBack);

      const { data: previousProfiles } = await supabase
        .from('profiles')
        .select('id')
        .gte('created_at', previousStartDate.toISOString())
        .lt('created_at', startDate.toISOString());

      const previousUsers = previousProfiles?.length || 0;
      const userGrowthRate = previousUsers > 0 ? ((recentUsers - previousUsers) / previousUsers) * 100 : 0;

      setStats({
        totalUsers,
        totalStudents,
        totalMentors,
        userGrowthRate,
        averageSessionsPerUser,
        mentorUtilizationRate: 0 // Will calculate when we have mentor activity data
      });

      // Fetch specialty data
      const { data: mentors } = await supabase
        .from('mentors')
        .select('specialty');

      const specialtyCount: { [key: string]: number } = {};
      mentors?.forEach(mentor => {
        if (mentor.specialty) {
          specialtyCount[mentor.specialty] = (specialtyCount[mentor.specialty] || 0) + 1;
        }
      });

      const specialtyChartData = Object.entries(specialtyCount)
        .map(([specialty, count]) => ({ specialty, count, sessions: 0 }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

      setSpecialtyData(specialtyChartData);

    } catch (error) {
      console.error('Error fetching growth data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>User Growth & Engagement</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-64 bg-muted rounded-lg"></div>
              <div className="h-64 bg-muted rounded-lg"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>User Growth & Engagement</span>
          </CardTitle>
          <div className="flex space-x-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === range
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted-foreground/10'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</p>
                </div>
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Students</p>
                  <p className="text-2xl font-bold">{stats.totalStudents.toLocaleString()}</p>
                </div>
                <UserPlus className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Mentors</p>
                  <p className="text-2xl font-bold">{stats.totalMentors.toLocaleString()}</p>
                </div>
                <Star className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Sessions/User</p>
                  <p className="text-2xl font-bold">{stats.averageSessionsPerUser.toFixed(1)}</p>
                </div>
                <Activity className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Growth Chart */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">User Growth Trends</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    className="text-muted-foreground text-xs"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    className="text-muted-foreground text-xs"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="new_users" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    name="New Users"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sessions_booked" 
                    stroke="hsl(var(--secondary))" 
                    strokeWidth={2}
                    name="Sessions Booked"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Popular Specialties */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Popular Specialties</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={specialtyData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="specialty" 
                    className="text-muted-foreground text-xs"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    className="text-muted-foreground text-xs"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="hsl(var(--primary))"
                    name="Mentors"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Growth Rate Badge */}
        <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <span className="font-medium">User Growth Rate</span>
          </div>
          <Badge variant={stats.userGrowthRate >= 0 ? "default" : "destructive"}>
            {stats.userGrowthRate >= 0 ? '+' : ''}{stats.userGrowthRate.toFixed(1)}%
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}